import {
  ascendingComparator,
  descendingComparator,
  groupContinuousValues,
  isEmpty,
  isNotEmpty,
  sortBy,
  sortByProperties,
  uniqueBy,
} from 'content-creation-common/utils/array';

export {
  ascendingComparator,
  descendingComparator,
  groupContinuousValues,
  isEmpty,
  isNotEmpty,
  sortBy,
  sortByProperties,
  uniqueBy,
};

import { isNumeric } from './numbers';
import { isArray, isNil, isUint8Array, deepEqual } from './object';
import { warn } from './log';

export const chunk = (array, size) => {
  const length = array.length;
  const chunked = [];

  for (let i = 0; i < length; i += size) {
    const arrayChunk = array.slice(i, i + size);
    chunked.push(arrayChunk);
  }

  return chunked;
};

export const sum = (array) => {
  if (!array || !(array instanceof Array)) return 0;
  return array.reduce((acc, cur) => {
    if (!isNumeric(cur)) {
      throw new Error('Trying to sum non numeric values');
    }
    return acc + Number(cur);
  }, 0);
};

const stringComparison = (a, b) => {
  if (a.toLowerCase() < b.toLowerCase()) {
    return -1;
  }

  if (b.toLowerCase() < a.toLowerCase()) {
    return 1;
  }

  return 0;
};

export const groupBy = (array, key) => {
  if (!isArray(array)) {
    warn('Cannot group something that is not an array');
    return {};
  }
  return array.reduce(
    (result, item) => ({
      ...result,
      [item[key]]: [...(result[item[key]] || []), item],
    }),
    {},
  );
};

export const sortAlphabeticallyBy = (array, key) => {
  return sortBy(array, key, stringComparison);
};

/**
 * Finds the most common value in an array of primitives. If the passed in `arr` is an array of
 *   objects, then the most common `property` will be returned.
 *
 * @param  {*[]} arr         - Array of values to find most common value within
 * @param  {String} property - Optional arg to select a property from the objects within arr
 * @return {* | undefined}   - most common value
 */
export const mostCommonValueInArr = (arr, property) => {
  if (!arr.length) {
    return undefined;
  }

  const mappingFunc =
    property !== undefined ? (obj) => obj[property] : (value) => value;
  const filteredArr = arr.map(mappingFunc).filter((el) => el !== undefined);

  if (!filteredArr.length) {
    return undefined;
  }

  const occurrences = {};

  let max = {
    element: undefined,
    value: -1,
  };

  for (let i = 0; i < filteredArr.length; i++) {
    const currElement = filteredArr[i];
    const prevOccurrences = occurrences[currElement];
    const nextOccurrences = prevOccurrences ? prevOccurrences + 1 : 1;
    occurrences[currElement] = nextOccurrences;

    if (nextOccurrences > max.value) {
      max = {
        element: currElement,
        value: nextOccurrences,
      };
    }
  }

  return max.element;
};

export const reorder = (arr, oldIndex, newIndex) => {
  arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
  return arr;
};

/**
 * Removes value at index from array (in place)
 */
export const removeAtIndex = (arr, index) => {
  if (index !== -1) {
    arr.splice(index, 1);
  }
};

/**
 * Removes value from array (in place) if found
 */
export const removeValue = (arr, value) => {
  const index = arr.indexOf(value);
  removeAtIndex(arr, index);
};

/**
 * Inserts value at index from array (in place)
 */
export const insertAtIndex = (arr, index, value) => {
  if (index !== -1) {
    arr.splice(index, 0, value);
  }
};

/**
 * insert value (in place) before a certain value if found
 */
export const insertBeforeValue = (arr, insertBeforeValue, value) => {
  const index = arr.indexOf(insertBeforeValue);
  if (index !== -1) {
    arr.splice(index, 0, value);
  }
};

/**
 * insert values (in place) before a given index
 */
export const insertAfterIndex = (arr, index, values) => {
  if (index !== -1) {
    // insert array of values
    if (isArray(values)) {
      for (let i = values.length - 1; i >= 0; i--) {
        arr.splice(index + 1, 0, values[i]);
      }
    } else {
      // insert one value
      arr.splice(index + 1, 0, values);
    }
  }
};

/**
 * insert values (in place) before a certain value if found
 */
export const insertAfterValue = (arr, insertBeforeValue, values) => {
  const index = arr.indexOf(insertBeforeValue);
  insertAfterIndex(arr, index, values);
};

/**
 * returns a new array containing items in arr that are not in values
 * @param arr
 * @param values
 */
export const difference = (arr, values) => {
  if (!isArray(arr) || !isArray(values)) return [];

  return arr.filter((item) => !values.includes(item));
};

/**
 * Shuffles the contents of an array in place.
 * Found here: https://stackoverflow.com/questions/6274339/how-can-i-shuffle-an-array/6274398
 * @param arr
 * @returns {Array}
 */
export const shuffle = (arr) => {
  if (isNil(arr) || !isArray(arr)) return;
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
};

export const toHexString = (arr) => {
  if (isNil(arr) || !isUint8Array(arr)) return undefined;
  let hexString = '';
  for (let i = 0; i < arr.length; i++) {
    hexString += arr[i].toString(16);
  }
  return hexString;
};

export const average = (arr) => {
  if (isNil(arr)) return undefined;

  let total = 0;
  const count = arr.length;
  for (let i = 0; i < count; i++) {
    const value = arr[i];
    // TODO (lemieux) warn if not a number
    total += value;
  }

  return total / count;
};

export const containsElement = (arr, elem) => {
  return !!arr.find((arrayElem) => {
    return deepEqual(arrayElem, elem);
  });
};

export const initializeArray = (length = 0, fillFn = (_) => 0) =>
  [...Array(length)].map((e, i) => fillFn(i));

export const isIndexInBounds = (arr, idx) =>
  isArray(arr) && idx >= 0 && idx < arr.length;
